'use client';

import React, { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Typography, Tooltip, Chip } from 'src/components';
import { Dropdown } from 'src/components/PulseUI';

/**
 * NewProviderListTags
 * @param {*} { lists }
 * @return {*}
 */

// Tag styles as Tailwind classes
const tagClassName =
  'h-7 min-w-12 whitespace-nowrap pr-4 bg-neutral-200 border border-black/20 rounded-full rounded-bl-none rounded-tr-none -ml-5 first:ml-0 only:pr-2 hover:bg-neutral-200 hover:cursor-pointer';

// TagButton component with Tailwind styles
const TagButton = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => (
  <button
    onClick={onClick}
    type="button"
    className="h-7 max-h-7 min-h-7 rounded-full rounded-bl-none rounded-tr-none text-inherit border border-dotted border-black bg-white -ml-5 text-xs font-semibold cursor-pointer inline-flex items-center justify-center px-2"
    data-testid="tag-button-surplus"
  >
    {children}
  </button>
);

const NewProviderListTags = ({ lists = [] }: { lists: MedScout.List[] }) => {
  const router = useRouter();
  const visibleTags = 3;

  const handleItemClick = (list: MedScout.List) => {
    const listPath = `/lists/${
      list.owner.id === list.owner.id ? 'my' : 'shared'
    }/${list.id}`;

    router.push(listPath);
  };

  const { displayedLists, remainingLists } = useMemo(() => {
    return {
      displayedLists: lists.slice(0, visibleTags),
      remainingLists: lists.slice(visibleTags),
    };
  }, [lists]);

  return (
    <div className="flex" data-testid="provider-list-tags">
      {displayedLists?.map((list, index) => {
        const fullName = [
          [list?.owner?.first_name, list.owner.last_name].join(' '),
          list.name,
        ].join(' > ');
        return (
          <Tooltip key={index} content={fullName}>
            <Chip
              onClick={() => handleItemClick(list)}
              aria-label={`Navigate to ${list.name} list`}
              className={tagClassName}
            >
              <Typography
                variant="caption"
                className="overflow-hidden text-ellipsis px-2"
              >
                {list.name}
              </Typography>
            </Chip>
          </Tooltip>
        );
      })}
      {remainingLists?.length > 0 && (
        <Dropdown
          options={remainingLists.map((list) => ({
            label: list.name,
            onClick: () => handleItemClick(list),
          }))}
          slots={{
            trigger: {
              component: (
                <TagButton>
                  <Typography variant="caption" bold>
                    +{lists.length - visibleTags}
                  </Typography>
                </TagButton>
              ),
            },
            option: {
              component: (option) => (
                <Typography variant="body2">{option.label}</Typography>
              ),
              className: 'border-b border-neutral-100 last:border-b-0',
            },
          }}
          position="bottom-right"
          className="max-h-[300px] overflow-auto z-[9999]"
          ariaLabel="Additional lists menu"
        />
      )}
    </div>
  );
};

export default NewProviderListTags;
